import React from "react";

import usePageMeta from "../utils/usePageMeta";
import { SITE_NAME, CONTACT_EMAIL, POLICY_UPDATED } from "../utils/site";

export default function PrivacyPolicy() {
  usePageMeta({
    title: "Privacy policy",
    description: `What ${SITE_NAME} collects, why, who it is shared with, and how to have it deleted.`,
    path: "/privacy-policy",
  });

  return (
    <div className="page prose">
      <h1>Privacy policy</h1>
      <p className="policy-date">Last updated {POLICY_UPDATED}</p>

      <p>
        This policy describes what {SITE_NAME} collects and why. The short
        version: we collect as little as the site can function on, we do not sell
        it, and you can have it removed by asking.
      </p>

      <h2>What we collect</h2>
      <p>
        <strong>If you never create an account,</strong> we ask nothing of you.
        You can read results, analytics and the community feed without
        identifying yourself.
      </p>
      <p>
        <strong>If you create an account,</strong> we store the username you
        choose and a bcrypt hash of your password. We do not store your password
        itself and cannot recover it — a hash cannot be reversed, which is the
        point of using one. Bear in mind that your username is shown publicly
        next to anything you post, so choosing an email address as a username
        makes that address public.
      </p>
      <p>
        <strong>If you post or comment,</strong> we store what you wrote, your
        username, and the time you wrote it. This content is public and readable
        by anyone, including people who are not signed in.
      </p>
      <p>
        <strong>When you sign in,</strong> we set a single cookie holding a signed
        session token so you stay signed in. It is marked HttpOnly, so scripts on
        the page cannot read it, and it expires after seven days. We keep no
        server-side record of your sign-ins — no history of when or from where
        you logged in.
      </p>

      <h2>Advertising and cookies</h2>
      <p>
        We use Google AdSense. Third-party vendors, including Google, use cookies
        to serve ads based on your prior visits to this and other websites.
      </p>
      <p>
        Google's use of advertising cookies enables it and its partners to serve
        ads to you based on your visit to this site and other sites on the
        internet. You can opt out of personalised advertising by visiting{" "}
        <a href="https://www.google.com/settings/ads" rel="noopener noreferrer" target="_blank">
          Google Ads Settings
        </a>
        , or opt out of third-party vendors' use of cookies for personalised
        advertising at{" "}
        <a href="https://www.aboutads.info/choices/" rel="noopener noreferrer" target="_blank">
          aboutads.info
        </a>
        .
      </p>
      <p>
        We also use Vercel Analytics, which counts page views without cookies and
        without building a profile of individual visitors.
      </p>

      <h2>Who processes your data</h2>
      <p>
        Accounts and posts are stored in MongoDB Atlas. The site is hosted by
        Vercel. Draw results are fetched from the California Lottery's public
        results feed; that request is made by our server, so your browser is not
        exposed to them. Advertising is served by Google. We do not sell or rent
        your information to anyone.
      </p>

      <h2>Keeping and deleting your data</h2>
      <p>
        Posts and comments stay until they are deleted. You can delete your own
        posts and comments from the site at any time, and the author of a post
        can remove comments left on it. To have your account and everything
        attached to it deleted, email{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we will remove
        it. You may also ask what we hold about you.
      </p>

      <h2>Children</h2>
      <p>
        This site concerns a form of gambling restricted to adults. It is not
        directed at children, and we do not knowingly collect information from
        anyone under 18. If you believe a child has created an account, contact
        us and we will delete it.
      </p>

      <h2>Changes</h2>
      <p>
        If this policy changes we will update the date above. Continuing to use
        the site after a change means you accept the revised policy.
      </p>

      <h2>Contact</h2>
      <p>
        Questions about privacy can go to{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </div>
  );
}
